import { z } from "zod";

/**
 * A school's academic structure: academic years, classes (grade levels in order) and their
 * sections. Used by the API and the School settings screens, so both apply the same rules.
 */

// ---------------------------------------------------------------------------------------------
// Academic years
// ---------------------------------------------------------------------------------------------

export const ACADEMIC_YEAR_STATUSES = ["planned", "current", "closed"] as const;
export type AcademicYearStatus = (typeof ACADEMIC_YEAR_STATUSES)[number];

export const ACADEMIC_YEAR_STATUS_LABELS: Record<AcademicYearStatus, string> = {
  planned: "Planned",
  current: "Current",
  closed: "Closed",
};

const isoDate = (empty: string) =>
  z
    .string()
    .trim()
    .min(1, { error: empty, abort: true })
    .pipe(z.iso.date({ error: "Enter the date as YYYY-MM-DD" }));

/** "2026-27": the start year and the last two digits of the next one. */
export const academicYearNameSchema = z
  .string()
  .trim()
  .min(1, { error: "Enter the year, like 2026-27", abort: true })
  .regex(/^(\d{4})-(\d{2})$/, { error: "Write the year like 2026-27" })
  .refine(
    (value) => {
      const [start, end] = value.split("-");
      return (Number(start) + 1) % 100 === Number(end);
    },
    { error: "The second part is the next year, like 2026-27" },
  );

const yearFields = {
  name: academicYearNameSchema,
  startsOn: isoDate("Enter the first day"),
  endsOn: isoDate("Enter the last day"),
};

const endsAfterStart = (value: { startsOn?: string; endsOn?: string }) =>
  !value.startsOn || !value.endsOn || value.endsOn > value.startsOn;
const endIssue = {
  error: "The last day must be after the first day",
  path: ["endsOn"],
  when: (payload: { issues: readonly { path?: readonly PropertyKey[] }[] }) =>
    !payload.issues.some((issue) => ["startsOn", "endsOn"].includes(String(issue.path?.[0]))),
};

export const academicYearCreateSchema = z.object(yearFields).refine(endsAfterStart, endIssue);

export const academicYearUpdateSchema = z
  .object({ ...yearFields, version: z.number().int().min(1) })
  .refine(endsAfterStart, endIssue);

/** Making a year current closes the one that was current. */
export const academicYearCurrentSchema = z.object({ version: z.number().int().min(1) });

export type AcademicYearCreate = z.output<typeof academicYearCreateSchema>;
export type AcademicYearUpdate = z.output<typeof academicYearUpdateSchema>;

export interface AcademicYearDto {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
  status: AcademicYearStatus;
  version: number;
}

// ---------------------------------------------------------------------------------------------
// Classes and sections
// ---------------------------------------------------------------------------------------------

/** NEP 2020 stages, used to group classes (and later wings and report cards). */
export const CLASS_STAGES = [
  "pre_primary",
  "primary",
  "middle",
  "secondary",
  "senior_secondary",
] as const;
export type ClassStage = (typeof CLASS_STAGES)[number];

export const CLASS_STAGE_LABELS: Record<ClassStage, string> = {
  pre_primary: "Pre-primary",
  primary: "Primary (1–5)",
  middle: "Middle (6–8)",
  secondary: "Secondary (9–10)",
  senior_secondary: "Senior secondary (11–12)",
};

export const STREAMS = ["science", "commerce", "humanities"] as const;
export type Stream = (typeof STREAMS)[number];

export const STREAM_LABELS: Record<Stream, string> = {
  science: "Science",
  commerce: "Commerce",
  humanities: "Humanities",
};

export const RECORD_STATUSES = ["active", "retired"] as const;
export type RecordStatus = (typeof RECORD_STATUSES)[number];

/** "8" is shown as "Class 8"; "Nursery" or "LKG" as they are. */
export function classTitle(name: string): string {
  return /^\d{1,2}$/.test(name) ? `Class ${name}` : name;
}

/** "8-A", as on registers and ID cards. */
export function sectionLabel(className: string, sectionName: string): string {
  return `${className}-${sectionName}`;
}

/** Ready-made class lists a school can start from; existing names are skipped. */
export const CLASS_PRESETS = {
  nursery_to_12: ["Nursery", "LKG", "UKG", ...Array.from({ length: 12 }, (_, i) => `${i + 1}`)],
  lkg_to_12: ["LKG", "UKG", ...Array.from({ length: 12 }, (_, i) => `${i + 1}`)],
  one_to_12: Array.from({ length: 12 }, (_, i) => `${i + 1}`),
  one_to_10: Array.from({ length: 10 }, (_, i) => `${i + 1}`),
} as const;
export type ClassPreset = keyof typeof CLASS_PRESETS;

export const CLASS_PRESET_LABELS: Record<ClassPreset, string> = {
  nursery_to_12: "Nursery, LKG, UKG and Classes 1 to 12",
  lkg_to_12: "LKG, UKG and Classes 1 to 12",
  one_to_12: "Classes 1 to 12",
  one_to_10: "Classes 1 to 10",
};

/** The stage a class name usually belongs to; the school can change it. */
export function stageFor(name: string): ClassStage {
  const grade = Number(name);
  if (!Number.isInteger(grade)) return "pre_primary";
  if (grade <= 5) return "primary";
  if (grade <= 8) return "middle";
  if (grade <= 10) return "secondary";
  return "senior_secondary";
}

const className = z
  .string()
  .trim()
  .min(1, { error: "Enter the class name, like 8 or LKG" })
  .max(40, { error: "Class name is too long" });

export const classCreateSchema = z.object({
  name: className,
  stage: z.enum(CLASS_STAGES, { error: "Choose the stage" }),
});

export const classUpdateSchema = z.object({
  name: className,
  stage: z.enum(CLASS_STAGES, { error: "Choose the stage" }),
  status: z.enum(RECORD_STATUSES, { error: "Choose a status" }),
  version: z.number().int().min(1),
});

/** The classes in the order the school wants them shown (every class, once). */
export const classOrderSchema = z.object({
  ids: z
    .array(z.string().regex(/^cls_[0-9A-Z]{26}$/, { error: "Unknown class" }))
    .min(1)
    .max(60, { error: "Too many classes" })
    .refine((ids) => new Set(ids).size === ids.length, { error: "A class is listed twice" }),
});

export const classPresetSchema = z.object({
  preset: z.enum(Object.keys(CLASS_PRESETS) as [ClassPreset, ...ClassPreset[]], {
    error: "Choose a list of classes",
  }),
});

export type ClassCreate = z.output<typeof classCreateSchema>;
export type ClassUpdate = z.output<typeof classUpdateSchema>;

/** Optional whole number in a range: blank becomes null. */
const optionalCount = (max: number) =>
  z.preprocess(
    (value) => (value === "" || value === undefined ? null : value),
    z.coerce
      .number({ error: "Enter a number" })
      .int({ error: "Enter a whole number" })
      .min(1, { error: "Enter at least 1" })
      .max(max, { error: `At most ${max}` })
      .nullable(),
  );

const sectionFields = {
  name: z
    .string()
    .trim()
    .toUpperCase()
    .min(1, { error: "Enter the section, like A", abort: true })
    .max(10, { error: "Section name is too long" })
    .regex(/^[A-Z0-9][A-Z0-9 -]*$/, { error: "Use letters and numbers, like A or ROSE" }),
  classTeacherId: z
    .string()
    .trim()
    .regex(/^usr_[0-9A-Z]{26}$/, { error: "Choose a teacher" })
    .nullable()
    .default(null),
  capacity: optionalCount(200),
  room: z
    .string()
    .trim()
    .max(40, { error: "Room is too long" })
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .default(null),
  stream: z.enum(STREAMS, { error: "Choose a stream" }).nullable().default(null),
};

export const sectionCreateSchema = z.object({
  classId: z.string().regex(/^cls_[0-9A-Z]{26}$/, { error: "Choose a class" }),
  ...sectionFields,
});

export const sectionUpdateSchema = z.object({
  ...sectionFields,
  status: z.enum(RECORD_STATUSES, { error: "Choose a status" }),
  version: z.number().int().min(1),
});

export type SectionCreate = z.output<typeof sectionCreateSchema>;
export type SectionCreateInput = z.input<typeof sectionCreateSchema>;
export type SectionUpdate = z.output<typeof sectionUpdateSchema>;

export interface ClassSectionDto {
  id: string;
  name: string;
  /** "8-A" */
  label: string;
  classTeacher: { id: string; displayName: string } | null;
  capacity: number | null;
  room: string | null;
  stream: Stream | null;
  status: RecordStatus;
  /** Active students in the section now. */
  studentCount: number;
  version: number;
}

export interface ClassDto {
  id: string;
  name: string;
  stage: ClassStage;
  position: number;
  status: RecordStatus;
  version: number;
  sections: ClassSectionDto[];
}

/** Someone who can be made a class teacher: an active teacher, principal or admin. */
export interface StaffOptionDto {
  id: string;
  displayName: string;
  loginId: string;
}
